/**
 * Overview tab of the site detail page.
 *
 * Three states:
 *  - No live version (html): the creation panel (SiteCreatePanel) with the
 *    three publish paths. The page-wide drag-drop and the deploy state in
 *    AeroSiteDetail behave exactly as before.
 *  - Live html site: the preview (snapshot first, live site on request), the publish dropzone and the
 *    auto-publish switch, unchanged behaviour, plus the one-line
 *    latest-version summary that links to the Versions tab.
 *  - Figma site: the preview (snapshot first, live site on request) plus a source row (the embedded
 *    prototype with a Change action); no dropzone or version summary, since
 *    figma sites have no version history.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AeroSwitch from '../../../components/Aero/AeroSwitch';
import SiteCreatePanel from '../SiteCreatePanel';
import AgentPublishModal from '../../../components/shared/AgentPublishModal';
import SitePreviewHero from './SitePreviewHero';
import SiteDropZone from '../SiteDropZone';
import SiteTestsCard from '../SiteTestsCard';

function SiteOverviewTab({
  user,
  site,
  versions,
  liveUrl,
  liveHost,
  deploying,
  progress,
  autoPublish,
  setAutoPublish,
  onDrop,
  fetchSite,
  fetchVersions,
  siteTests = null,
  onTestSite = null,
  testing = false,
}) {
  const isFigma = site.type === 'figma';
  const navigate = useNavigate();
  const [showAgentModal, setShowAgentModal] = useState(false);

  const refresh = async () => {
    await fetchSite();
    await fetchVersions();
  };

  // Figma sites serve through the folio renderer; their overview is the
  // preview plus the source row.
  if (isFigma) {
    const embedUrl = site.urls?.figmaDesktopUrl || site.urls?.figmaMobileUrl || '';
    return (
      <>
        <SitePreviewHero site={site} host={liveHost} liveUrl={liveUrl} iframeKey={embedUrl} fetchSite={fetchSite} />

        {/* Read-only source summary; the single editable home for the
            prototype (source switch + breakpoints editor) is the Prototype
            tab this button links to. */}
        <div className="aero-card aero-card-pad">
          <div className="aero-section-label">Source</div>
          <div className="aero-setting-row">
            <div>
              <h4>Figma prototype (embedded)</h4>
              <p className="aero-sites-source-link">{embedUrl}</p>
            </div>
            <button
              type="button"
              className="aero-btn aero-btn-secondary aero-btn-sm"
              onClick={() => navigate(`/sites/${site.id}/prototype`)}
            >
              Edit prototype
            </button>
          </div>
        </div>
      </>
    );
  }

  const isLive = !!site.activeVersion;

  // No live version yet: the creation panel replaces the old bare dropzone.
  if (!isLive) {
    return (
      <SiteCreatePanel
        user={user}
        site={site}
        deploying={deploying}
        progress={progress}
        onDrop={onDrop}
        onEmbedded={refresh}
      />
    );
  }

  return (
    <>
      {/* Two-column row: live preview (left) + publish update (right) */}
      <div className="aero-sites-detail-cols">
        {/* LEFT, the preview: the stored snapshot, or the live site on request */}
        <SitePreviewHero site={site} host={liveHost} liveUrl={liveUrl} iframeKey={site.activeVersion} fetchSite={fetchSite} />

        {/* RIGHT, publish update */}
        <div className="aero-sites-publish">
          <SiteDropZone
            onDrop={onDrop}
            deploying={deploying}
            disabled={!site}
            progress={progress}
            title="Drop a new version"
            hint={autoPublish
              ? 'A folder, zip or a few files. It replaces the whole site at the same address; the old version is kept.'
              : 'A folder, zip or a few files. It is saved as a draft you can publish from Versions below.'}
          />

          {/* Auto-publish, its own row, directly under the dropzone. */}
          <div className="aero-setting-row aero-sites-autopublish">
            <div>
              <h4>Publish automatically when I upload</h4>
              <p>
                Turn this off to save the upload as a draft you can publish later.
              </p>
            </div>
            <AeroSwitch
              checked={autoPublish}
              onChange={setAutoPublish}
              disabled={deploying}
            />
          </div>

          {/* Update with an agent: an id-targeted agent prompt for THIS site,
              so a coding agent can push the next version straight to it. */}
          <div className="aero-setting-row aero-sites-update-agent">
            <div>
              <h4>Update with a prompt</h4>
              <p>
                Paste a prompt into Claude or ChatGPT and it builds and publishes
                the next version straight to this site.
              </p>
            </div>
            <button
              type="button"
              className="aero-btn aero-btn-secondary aero-btn-sm"
              onClick={() => setShowAgentModal(true)}
            >
              Get prompt
            </button>
          </div>
        </div>
      </div>

      {/* Tests of this site: its test copies (and an older test kept on the
          site itself). Owned html sites only; null while loading. */}
      {siteTests && (
        <SiteTestsCard
          tests={siteTests}
          onOpen={(id) => navigate(`/user-testing/${id}`)}
          onTest={onTestSite}
          testing={testing}
        />
      )}

      {showAgentModal && (
        <AgentPublishModal
          user={user}
          site={site}
          heading="Update with a prompt"
          onClose={() => setShowAgentModal(false)}
        />
      )}
    </>
  );
}

export default SiteOverviewTab;
